import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useFuzzyRate, fuzzyToXrp, fmtXrpApprox, fmtFuzzyPerXrp } from '../hooks/useFuzzyRate';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, Users, DollarSign, Wallet, Sun, Moon, Gift, Package, Star, Shield, Pencil } from 'lucide-react';
import { PhotoGallery } from '../components/PhotoGallery';
import { AuthenticityPanel } from '../components/AuthenticityBadge';
import { NftTrustPanel } from '../components/NftTrustBadge';
import { ListingActions, ListingMeta } from '../components/ListingActions';
import MakeOfferDialog from '../components/MakeOfferDialog';
import EditListingDialog from '../components/EditListingDialog';

const tg = () => window.Telegram?.WebApp;
const haptic = (type = 'impact', style = 'light') => {
  try {
    if (type === 'impact') tg()?.HapticFeedback?.impactOccurred(style);
    else if (type === 'notification') tg()?.HapticFeedback?.notificationOccurred(style);
  } catch (e) {}
};

function GameRoom() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, updateBalance, updateBalances, loading: authLoading } = useAuth();
  const [game, setGame] = useState(null);
  const [middaySession, setMiddaySession] = useState(null);
  const [eveningSession, setEveningSession] = useState(null);
  const [selectedDrawType, setSelectedDrawType] = useState('midday');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBinDialog, setShowBinDialog] = useState(false);
  const [binLoading, setBinLoading] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCloseEarlyDialog, setShowCloseEarlyDialog] = useState(false);
  const [closingEarly, setClosingEarly] = useState(false);
  const [firstBuyerQty, setFirstBuyerQty] = useState('');
  const fuzzyRate = useFuzzyRate(game?.currency === 'FUZZY' || game?.bin_flex_currency);
  const [binCurrency, setBinCurrency] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      toast.error('Please login first');
      navigate('/');
      return;
    }
    fetchGameSessions();

    // Telegram BackButton
    const telegram = tg();
    if (telegram) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(() => navigate('/'));
    }

    const interval = setInterval(fetchGameSessions, 10000);
    return () => {
      clearInterval(interval);
      if (telegram) {
        telegram.BackButton.hide();
        telegram.BackButton.offClick(() => navigate('/'));
      }
    };
  }, [gameId, isAuthenticated, authLoading]);

  const fetchGameSessions = async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}/sessions`);
      setGame(response.data.game);
      setMiddaySession(response.data.midday);
      setEveningSession(response.data.evening);
    } catch (error) {
      console.error('Error fetching game sessions:', error);
      toast.error('Failed to load game');
    }
  };

  const handleSlotClick = (slot, drawType) => {
    if (currentSession.session?.early_close) {
      haptic('notification', 'error');
      toast.info(`Session closed by host — winner drawn at next ${drawType} Fireball`);
      return;
    }
    if (!slot.available) {
      haptic('notification', 'error');
      const isOwned = slot.owner === user?.user_id;
      if (isOwned) {
        toast.info(`Slot ${slot.slot_number} is yours — locked in for ${drawType} draw`);
      } else if (slot.is_ghost) {
        toast.info(`Slot ${slot.slot_number} taken by Ghost Player`);
      } else {
        toast.error(`Slot ${slot.slot_number} taken${slot.owner_username ? ` by @${slot.owner_username}` : ''}`);
      }
      return;
    }
    haptic('impact', 'medium');
    setSelectedSlot({ ...slot, drawType });
    setShowConfirmDialog(true);
  };

  const handlePurchaseSlot = async () => {
    if (!selectedSlot) return;
    const sess = selectedSlot.drawType === 'midday' ? middaySession : eveningSession;
    const needQty = game?.price_mode === 'first_buyer' && (sess?.session?.custom_entry_price == null);
    if (needQty) {
      const q = parseFloat(firstBuyerQty);
      if (!q || q < 1) {
        toast.error(`You're the first bear in! Set the ${game?.currency || 'FUZZY'} price per slot (min 1).`);
        return;
      }
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/games/${gameId}/buy-slot`,
        {
          slot_number: selectedSlot.slot_number,
          draw_type: selectedSlot.drawType,
          ...(needQty ? { quantity: parseFloat(firstBuyerQty) } : {}),
        }
      );
      if (response.data.status === 'success') {
        haptic('notification', 'success');
        toast.success(needQty
          ? `Slot ${selectedSlot.slot_number} purchased — you set the price at ${response.data.entry_price} ${game?.currency || 'FUZZY'}!`
          : `Slot ${selectedSlot.slot_number} purchased!`);
        updateBalances({ balance: response.data.new_balance, fuzzy_balance: response.data.new_fuzzy_balance });
        setFirstBuyerQty('');
        await fetchGameSessions();
        setShowConfirmDialog(false);
        setSelectedSlot(null);
      }
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEarly = async () => {
    setClosingEarly(true);
    try {
      const response = await axios.post(
        `${API}/hosted-games/${gameId}/close-early`,
        { draw_type: selectedDrawType }
      );
      if (response.data.status === 'success') {
        haptic('notification', 'success');
        toast.success(response.data.message);
        await fetchGameSessions();
        setShowCloseEarlyDialog(false);
      }
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed to close early');
    } finally {
      setClosingEarly(false);
    }
  };

  const handleBIN = async () => {
    setBinLoading(true);
    try {
      const response = await axios.post(
        `${API}/games/${gameId}/bin`,
        { draw_type: selectedDrawType, currency: binCurrency || currency }
      );
      if (response.data.status === 'success') {
        haptic('notification', 'success');
        toast.success(response.data.message);
        updateBalances({ balance: response.data.new_balance, fuzzy_balance: response.data.new_fuzzy_balance });
        await fetchGameSessions();
        setShowBinDialog(false);
      }
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'BIN failed');
    } finally {
      setBinLoading(false);
    }
  };

  if (!game || !middaySession || !eveningSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto"></div>
      </div>
    );
  }

  const currentSession = selectedDrawType === 'midday' ? middaySession : eveningSession;
  const currentSlots = currentSession.slots;
  const availableSlots = currentSlots.filter(s => s.available).length;
  const currency = game.currency === 'FUZZY' ? 'FUZZY' : 'XRP';
  const isFuzzy = currency === 'FUZZY';
  const balance = isFuzzy ? (user?.fuzzy_balance || 0) : (user?.balance || 0);
  const isFirstBuyerRoom = game.price_mode === 'first_buyer';
  const sessionPrice = currentSession?.session?.custom_entry_price ?? null;
  const priceNotSet = isFirstBuyerRoom && sessionPrice === null;
  const effectiveCost = isFirstBuyerRoom ? (sessionPrice ?? 0) : game.total_cost;
  const dialogCost = priceNotSet ? (parseFloat(firstBuyerQty) || 0) : effectiveCost;

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                data-testid="back-button"
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-white p-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-base font-bold text-white leading-tight">{game.room_name}</h1>
                <p className="text-[11px] text-slate-500">
                  {priceNotSet ? '🍯 First buyer sets the price' : `Entry: ${effectiveCost} ${currency}`}
                </p>
                {isFuzzy && fuzzyRate && (
                  <p data-testid="fuzzy-live-rate" className="text-[10px] text-amber-300/90 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
                    {fmtFuzzyPerXrp(fuzzyRate)} · live XRPL DEX
                  </p>
                )}
              </div>
            </div>
            <div data-testid="game-balance" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
              <Wallet className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-sm font-semibold text-orange-300">{isFuzzy ? `${balance.toLocaleString(undefined, { maximumFractionDigits: 0 })} FUZZY` : `${balance.toFixed(2)} XRP`}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Draw Type Toggle */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex gap-2 bg-slate-800/50 p-1 rounded-xl">
          <button
            data-testid="midday-tab"
            onClick={() => { haptic(); setSelectedDrawType('midday'); }}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              selectedDrawType === 'midday'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                : 'text-slate-400'
            }`}
          >
            <Sun className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Midday
          </button>
          <button
            data-testid="evening-tab"
            onClick={() => { haptic(); setSelectedDrawType('evening'); }}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              selectedDrawType === 'evening'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md'
                : 'text-slate-400'
            }`}
          >
            <Moon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Evening
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="px-4 py-2 flex gap-2">
        <div data-testid="prize-pool-card" className="flex-1 bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-center">
          <Trophy className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-emerald-400">{currentSession.pool_amount.toFixed(1)}</p>
          <p className="text-[10px] text-slate-500">Pool ({currency})</p>
          {isFuzzy && fuzzyRate && currentSession.pool_amount > 0 && (
            <p className="text-[9px] text-emerald-300/70">{fmtXrpApprox(fuzzyToXrp(currentSession.pool_amount, fuzzyRate))}</p>
          )}
        </div>
        <div data-testid="slots-card" className="flex-1 bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-center">
          <Users className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-indigo-400">{availableSlots}/10</p>
          <p className="text-[10px] text-slate-500">Available</p>
        </div>
        <div data-testid="entry-cost-card" className="flex-1 bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-center">
          <DollarSign className="w-4 h-4 text-orange-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-orange-400">{priceNotSet ? '🍯' : effectiveCost}</p>
          <p className="text-[10px] text-slate-500">{priceNotSet ? 'Price not set yet' : `Cost (${currency})`}</p>
        </div>
      </div>

      {/* Prize Info for Hosted Games */}
      {game.is_hosted && (
        <div className="px-4 py-2 space-y-2">
          {/* Photo gallery */}
          {game.photo_urls && game.photo_urls.length > 0 && (
            <PhotoGallery photos={game.photo_urls} alt={game.room_name} />
          )}
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {game.prize_type === 'nft' ? <Star className="w-4 h-4 text-purple-400" /> : <Package className="w-4 h-4 text-purple-400" />}
              <span className="text-xs font-semibold text-purple-300 uppercase">{game.prize_type} Prize</span>
              {game.escrow_required && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 font-medium flex items-center gap-0.5">
                  <Shield className="w-2.5 h-2.5" /> Escrow
                </span>
              )}
              {game.min_players && game.min_players >= 2 && (
                <span data-testid="game-min-players-badge" className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/25 font-medium flex items-center gap-0.5">
                  <Users className="w-2.5 h-2.5" /> Min {game.min_players} players
                </span>
              )}
            </div>
            <p className="text-sm text-white font-medium">{game.prize_description}</p>
            {game.prize_value_xrp && (
              <p className="text-[11px] text-slate-400 mt-1">Estimated value: <span className="text-amber-300 font-semibold">{game.prize_value_xrp} XRP</span></p>
            )}
            {game.min_players && game.min_players >= 2 && (
              <p className="text-[10px] text-amber-400/80 mt-1.5">
                Auto-refund if fewer than {game.min_players} real players join before draw.
              </p>
            )}
          </div>
          {/* Authenticity panel */}
          <AuthenticityPanel
            condition={game.condition}
            grade={game.grade}
            certNumber={game.cert_number}
            verified={game.authenticity_verified}
          />
          {/* NFT trust analysis */}
          {game.prize_type === 'nft' && game.nft_analysis && (
            <NftTrustPanel analysis={game.nft_analysis} />
          )}
          {/* Marketplace meta + actions */}
          <ListingMeta
            createdAt={game.created_at}
            location={game.host_location}
            prizeType={game.prize_type}
          />
          <ListingActions
            shareUrl={typeof window !== 'undefined' ? window.location.href : ''}
            shareTitle={`${game.room_name} — ${game.prize_description} · Win on Vitamin C Gainz`}
            hostUsername={game.host_username}
            hostTelegramUsername={game.host_telegram_username}
            hostUserId={game.host_user_id}
            targetType="hosted_game"
            targetId={game.game_id}
            targetName={game.room_name}
            isOwnListing={user?.user_id === game.host_user_id}
            onBlocked={() => navigate('/')}
          />
        </div>
      )}

      {/* BIN + Make Offer — only for hosted prize rooms with all slots available */}
      {game.is_hosted && availableSlots === 10 && user?.user_id !== game.host_user_id && (
        <div className="px-4 pb-2 space-y-2">
          <button
            data-testid="bin-button"
            onClick={() => { haptic('impact', 'heavy'); setShowBinDialog(true); }}
            className="w-full relative overflow-hidden rounded-xl border-2 border-red-500/40 bg-gradient-to-r from-red-500/10 to-orange-500/10 py-3 active:scale-[0.98] transition-all"
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-base font-black text-red-400 tracking-wider">BIN</span>
              <span className="text-[11px] text-slate-400">Buy It Now</span>
              <span className="text-sm font-bold text-orange-300">{(game.bin_price ?? game.total_cost * 10).toFixed(1)} XRP</span>
            </div>
            <p className="text-[9px] text-slate-500 mt-0.5">Skip the draw — win the prize instantly</p>
          </button>
          <button
            data-testid="make-offer-button"
            onClick={() => { haptic('impact', 'medium'); setShowOfferDialog(true); }}
            className="w-full rounded-xl border border-amber-500/40 bg-amber-500/5 py-2.5 active:scale-[0.98] transition-all"
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm font-bold text-amber-300 tracking-wider">MAKE OFFER</span>
              <span className="text-[10px] text-slate-400">— host has 48h to respond</span>
            </div>
          </button>
        </div>
      )}

      {/* Host Edit + Run Early buttons — only visible to the host on an active hosted listing */}
      {game.is_hosted && user?.user_id === game.host_user_id && game.status === 'active' && (
        <div className="px-4 pb-2 space-y-2">
          <button
            data-testid="host-edit-button"
            onClick={() => { haptic(); setShowEditDialog(true); }}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 py-2 text-xs font-bold text-amber-300 transition active:scale-[0.98]"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit Listing
          </button>
          {/* Run Game Early — only when selected draw's session has 2-9 real (non-ghost) slots sold */}
          {(() => {
            const realSold = currentSlots.filter(s => !s.available && !s.is_ghost).length;
            const alreadyFlagged = !!currentSession.session?.early_close;
            const eligible = realSold >= 2 && realSold < 10;
            if (alreadyFlagged) {
              return (
                <div data-testid="early-close-flagged-badge" className="w-full text-center rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-2 py-2 text-[11px] font-bold text-emerald-300 leading-snug break-words">
                  Flagged to run early ({realSold}/10) — winner drawn at next {selectedDrawType} Fireball
                </div>
              );
            }
            if (!eligible) return null;
            return (
              <button
                data-testid="host-run-early-button"
                onClick={() => { haptic('impact', 'medium'); setShowCloseEarlyDialog(true); }}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-orange-500/40 bg-orange-500/5 hover:bg-orange-500/10 py-2 text-xs font-bold text-orange-300 transition active:scale-[0.98]"
              >
                <Trophy className="w-3.5 h-3.5" /> Run Game Early ({realSold}/10 slots)
              </button>
            );
          })()}
        </div>
      )}

      {/* Buyer-facing banner when host has flagged early close */}
      {currentSession.session?.early_close && (
        <div data-testid="early-close-buyer-banner" className="mx-4 mb-2 rounded-xl border border-orange-500/40 bg-orange-500/5 px-3 py-2.5 text-[11px] text-orange-200/95 leading-snug">
          <p className="font-bold text-orange-300 mb-0.5">Session closed by host</p>
          <p>No more slots can be bought. Winner drawn at the next {selectedDrawType} Illinois Pick 3 Fireball. Empty slots don&apos;t participate — if the winning digit lands on an empty slot, all buyers are refunded.</p>
        </div>
      )}

      {/* Slot Grid - Mobile optimized 5x2 */}
      <div data-testid="slot-grid" className="px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-white">Pick your lucky number</p>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded border border-indigo-500"></span>Open</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-600"></span>Taken</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded ring-1 ring-emerald-500 bg-emerald-500/30"></span>Yours</span>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-3">
          {currentSlots.map((slot) => {
            const isOwned = slot.owner === user?.user_id;
            const flagged = !!currentSession.session?.early_close;
            const ownerLabel = slot.is_ghost
              ? 'Ghost'
              : isOwned
                ? 'You'
                : slot.owner_username
                  ? `@${slot.owner_username.length > 7 ? slot.owner_username.slice(0, 7) + '…' : slot.owner_username}`
                  : null;
            return (
              <button
                key={slot.slot_number}
                data-testid={`slot-${slot.slot_number}`}
                onClick={() => handleSlotClick(slot, selectedDrawType)}
                aria-label={
                  slot.available
                    ? `Pick slot ${slot.slot_number}`
                    : isOwned
                      ? `Slot ${slot.slot_number} owned by you`
                      : `Slot ${slot.slot_number} taken${slot.owner_username ? ` by ${slot.owner_username}` : ''}`
                }
                title={
                  flagged && slot.available
                    ? `Slot ${slot.slot_number} — session closed by host`
                    : slot.available
                      ? `Slot ${slot.slot_number} — open`
                      : isOwned
                        ? `Slot ${slot.slot_number} — yours`
                        : `Taken${slot.owner_username ? ` by @${slot.owner_username}` : ''}${slot.is_ghost ? ' (Ghost)' : ''}`
                }
                className={`
                  relative aspect-square rounded-2xl text-2xl font-bold transition-all duration-150 flex flex-col items-center justify-center
                  ${slot.available && !flagged
                    ? 'bg-slate-800/60 border-2 border-indigo-500/70 text-indigo-300 active:scale-95 active:bg-indigo-500/30 cursor-pointer'
                    : slot.available && flagged
                      ? 'bg-slate-800/30 border-2 border-slate-700 text-slate-600 cursor-not-allowed opacity-60'
                      : 'bg-slate-700/40 border-2 border-slate-600 text-slate-500 cursor-help'
                  }
                  ${isOwned ? 'ring-2 ring-emerald-500 bg-emerald-500/10 border-emerald-500/50 text-emerald-300' : ''}
                `}
              >
                <span className="leading-none">{slot.slot_number}</span>
                {!slot.available && ownerLabel && (
                  <span
                    data-testid={`slot-owner-${slot.slot_number}`}
                    className={`absolute bottom-1 left-1 right-1 text-[8px] font-semibold leading-none truncate ${
                      isOwned ? 'text-emerald-400' : slot.is_ghost ? 'text-slate-500 italic' : 'text-slate-400'
                    }`}
                  >
                    {ownerLabel}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Both Sessions Summary */}
      <div className="px-4 pb-2 flex gap-2">
        <div className="flex-1 bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sun className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-semibold text-white">Midday</span>
          </div>
          <div className="flex justify-between text-[11px]">
            <span className="text-slate-500">Pool</span>
            <span className="text-emerald-400 font-medium">{middaySession.pool_amount.toFixed(1)} {currency}</span>
          </div>
          <div className="flex justify-between text-[11px]">
            <span className="text-slate-500">Slots</span>
            <span className="text-indigo-400 font-medium">{middaySession.slots.filter(s => s.available).length}/10</span>
          </div>
        </div>
        <div className="flex-1 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Moon className="w-3.5 h-3.5 text-indigo-400" />
            <span className="text-xs font-semibold text-white">Evening</span>
          </div>
          <div className="flex justify-between text-[11px]">
            <span className="text-slate-500">Pool</span>
            <span className="text-emerald-400 font-medium">{eveningSession.pool_amount.toFixed(1)} {currency}</span>
          </div>
          <div className="flex justify-between text-[11px]">
            <span className="text-slate-500">Slots</span>
            <span className="text-indigo-400 font-medium">{eveningSession.slots.filter(s => s.available).length}/10</span>
          </div>
        </div>
      </div>

      {/* How it works - Collapsed */}
      <div className="px-4 pt-2 pb-4">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-3">
          <p className="text-xs font-semibold text-slate-400 mb-2">How it works</p>
          <div className="space-y-1.5 text-[11px] text-slate-500">
            <p><span className="text-orange-400 font-bold mr-1">1.</span>Pick Midday or Evening draw</p>
            <p><span className="text-orange-400 font-bold mr-1">2.</span>Choose slot 0-9 and purchase</p>
            <p><span className="text-orange-400 font-bold mr-1">3.</span>IL Pick 3 Fireball draws twice daily</p>
            <p><span className="text-orange-400 font-bold mr-1">4.</span>Match = you win the entire pool!</p>
          </div>
        </div>
      </div>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent data-testid="purchase-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Confirm Purchase</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Slot {selectedSlot?.slot_number} — {selectedSlot?.drawType === 'midday' ? 'Midday' : 'Evening'} Draw
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Draw</span>
                <span className="text-sm font-bold capitalize flex items-center gap-1">
                  {selectedSlot?.drawType === 'midday' ? <Sun className="w-3.5 h-3.5 text-amber-400" /> : <Moon className="w-3.5 h-3.5 text-indigo-400" />}
                  {selectedSlot?.drawType}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Slot</span>
                <span className="text-xl font-bold text-indigo-400">{selectedSlot?.slot_number}</span>
              </div>
              <div className="h-px bg-slate-700"></div>
              {priceNotSet ? (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-amber-300">🍯 You're the first bear in — set the price per slot!</p>
                  <Input
                    data-testid="first-buyer-qty-input"
                    type="number"
                    min="1"
                    placeholder={`${currency} per slot (e.g. 5000)`}
                    value={firstBuyerQty}
                    onChange={(e) => setFirstBuyerQty(e.target.value)}
                    className="bg-slate-800 border-amber-500/40 text-white h-10 text-sm"
                  />
                  <p className="text-[10px] text-slate-500">Everyone after you pays the same. Winner raids the whole pot.</p>
                  {fuzzyRate && (
                    <p data-testid="first-buyer-xrp-equiv" className="text-[10px] text-amber-300 font-semibold">
                      {parseFloat(firstBuyerQty) > 0
                        ? `${firstBuyerQty} FUZZY ${fmtXrpApprox(fuzzyToXrp(parseFloat(firstBuyerQty), fuzzyRate))}`
                        : fmtFuzzyPerXrp(fuzzyRate)} <span className="text-slate-500 font-normal">· live XRPL DEX</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Cost</span>
                  <span className="text-base font-semibold text-white">
                    {effectiveCost} {currency}
                    {isFuzzy && fuzzyRate && (
                      <span className="text-[10px] text-amber-300 font-normal ml-1.5">{fmtXrpApprox(fuzzyToXrp(effectiveCost, fuzzyRate))}</span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500">Balance</span>
                <span className="text-slate-400">{isFuzzy ? balance.toLocaleString(undefined, { maximumFractionDigits: 0 }) : balance.toFixed(2)} {currency}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500">After</span>
                <span className="text-emerald-400 font-medium">{isFuzzy ? (balance - dialogCost).toLocaleString(undefined, { maximumFractionDigits: 0 }) : (balance - dialogCost).toFixed(2)} {currency}</span>
              </div>
            </div>

            {balance < dialogCost && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-xs text-red-400">
                Insufficient balance. Deposit more {currency}.
              </div>
            )}

            <Button
              data-testid="confirm-purchase-button"
              onClick={handlePurchaseSlot}
              disabled={loading || balance < dialogCost || (priceNotSet && dialogCost < 1)}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 h-11 font-semibold"
            >
              {loading ? 'Processing...' : (priceNotSet ? 'Set Price & Buy Slot' : 'Confirm Purchase')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BIN Confirmation Dialog */}
      <Dialog open={showBinDialog} onOpenChange={setShowBinDialog}>
        <DialogContent data-testid="bin-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Buy It Now</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Instantly win the prize — no waiting for the draw
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {(() => {
              const roomBin = game.bin_price ?? game.total_cost * 10;
              const payCur = (game.bin_flex_currency && binCurrency) ? binCurrency : currency;
              const converted = payCur === currency ? roomBin
                : (fuzzyRate ? (currency === 'XRP' ? roomBin * fuzzyRate.fuzzy_per_xrp : roomBin * fuzzyRate.xrp_per_fuzzy) : null);
              const binPrice = converted ?? roomBin;
              const payBalance = payCur === 'FUZZY' ? (user?.fuzzy_balance || 0) : (user?.balance || 0);
              return (
            <>
            {game.bin_flex_currency && (
              <div className="grid grid-cols-2 gap-2">
                {['XRP', 'FUZZY'].map(c => (
                  <button
                    key={c}
                    data-testid={`bin-currency-${c.toLowerCase()}`}
                    onClick={() => setBinCurrency(c)}
                    className={`rounded-xl border p-2.5 text-center transition-colors ${payCur === c ? 'bg-amber-500/15 border-amber-500/50' : 'bg-slate-800/50 border-slate-700'}`}
                  >
                    <p className={`text-xs font-bold ${payCur === c ? 'text-amber-300' : 'text-white'}`}>Pay in {c}</p>
                    <p className="text-[9px] text-slate-500">{c === currency ? 'host\u2019s currency' : 'live DEX rate'}</p>
                  </button>
                ))}
              </div>
            )}
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3.5 text-center">
              <p className="text-[10px] text-slate-500 mb-1">BIN Price (all 10 slots)</p>
              <p className="text-2xl font-black text-orange-400">{converted == null ? '…' : `${binPrice.toLocaleString(undefined, { maximumFractionDigits: payCur === 'FUZZY' ? 0 : 2 })} ${payCur}`}</p>
              {payCur !== currency && (
                <p className="text-[9px] text-cyan-300/80 mt-0.5">⇄ {roomBin.toLocaleString()} {currency} at the live DEX rate</p>
              )}
              {game.bin_price != null && (
                <p className="text-[9px] text-amber-300/70 mt-0.5">Host-set price</p>
              )}
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Prize</span>
                <span className="text-white font-medium">{game.prize_description}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Type</span>
                <span className="text-purple-300 uppercase text-xs font-semibold">{game.prize_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Draw</span>
                <span className="text-white capitalize">{selectedDrawType}</span>
              </div>
              <div className="h-px bg-slate-700"></div>
              <div className="flex justify-between">
                <span className="text-slate-400">Your balance</span>
                <span className="text-slate-300">{payBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} {payCur}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">After BIN</span>
                <span className={`font-medium ${payBalance >= binPrice ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(payBalance - binPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })} {payCur}
                </span>
              </div>
            </div>
            {payBalance < binPrice && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-xs text-red-400">
                Insufficient balance. You need {binPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} {payCur}.
              </div>
            )}
            <Button
              data-testid="confirm-bin-button"
              onClick={handleBIN}
              disabled={binLoading || payBalance < binPrice || converted == null}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 h-11 font-bold text-base"
            >
              {binLoading ? 'Processing...' : 'Confirm BIN'}
            </Button>
            <p className="text-[9px] text-slate-600 text-center">Prize will be delivered via {game.prize_type === 'physical' ? 'escrow-protected shipping' : 'on-chain NFT transfer'}</p>
            </>
            ); })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Make Offer Dialog */}
      <MakeOfferDialog
        open={showOfferDialog}
        onOpenChange={setShowOfferDialog}
        game={game}
        userBalance={balance}
        onSuccess={() => {}}
      />

      {/* Host Edit Dialog */}
      <EditListingDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        kind="hosted"
        listing={game}
        onUpdated={(fresh) => { if (fresh) setGame(fresh); }}
      />

      {/* Run Game Early Confirmation Dialog */}
      <Dialog open={showCloseEarlyDialog} onOpenChange={setShowCloseEarlyDialog}>
        <DialogContent data-testid="close-early-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Run Game Early?</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Draw the winner at the next Illinois Pick 3 Fireball with only the currently sold slots.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Draw</span>
                <span className="text-white capitalize">{selectedDrawType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Slots sold</span>
                <span className="text-emerald-300 font-semibold">{10 - availableSlots}/10</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Empty slots</span>
                <span className="text-slate-300">{availableSlots} — will not participate</span>
              </div>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg p-3 text-[11px] text-amber-200/90 leading-relaxed">
              <p className="font-semibold text-amber-300 mb-1">How it works</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>No more slots can be sold on this draw once flagged</li>
                <li>If the Fireball digit matches a sold slot → that player wins the pool</li>
                <li>If the Fireball digit is an empty slot → all buyers get fully refunded</li>
              </ul>
            </div>
            <Button
              data-testid="confirm-run-early-button"
              onClick={handleCloseEarly}
              disabled={closingEarly}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 h-11 font-semibold"
            >
              {closingEarly ? 'Flagging...' : `Flag ${selectedDrawType} session to run early`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GameRoom;
